import type {
  DocumentType,
  LegKind,
  OrderStatus,
  StopKind,
  StopStatus,
  TachographGeneration,
  TripEventType,
  TripStatus,
} from '@kmdx/contracts';

/**
 * Целият интерфейс е на български. Речникът е обикновен типизиран обект —
 * при два диспечера и един език цяла i18n рамка е разход без насрещна полза.
 * Ако утре потрябва втори език, тук се добавя втори обект със същия тип.
 */
export const bg = {
  app: {
    name: 'КМД ЕКСПРЕС',
    subtitle: 'Диспечерски пулт',
    company: '„КМД ЕКСПРЕС" ООД',
  },
  nav: {
    board: 'Начало',
    orders: 'Заявки',
    quote: 'Оферта',
    trips: 'Курсове',
    map: 'Карта',
    documents: 'ЧМР и документи',
    partners: 'Контрагенти',
    vehicles: 'Бусове',
    drivers: 'Шофьори',
    reports: 'Справки',
    dues: 'Падежи',
    fuel: 'Гориво',
    integrations: 'Frotcom и връзки',
    groupWork: 'Работа',
    groupMoney: 'Документи и пари',
    groupSetup: 'Настройки',
  },
  common: {
    search: 'Търсене',
    searchPlaceholder: 'Заявка, номер на клиента, регистрация, шофьор…',
    save: 'Запази',
    cancel: 'Отказ',
    close: 'Затвори',
    confirm: 'Потвърди',
    delete: 'Изтрий',
    edit: 'Редактирай',
    create: 'Създай',
    add: 'Добави',
    remove: 'Премахни',
    back: 'Назад',
    next: 'Напред',
    loading: 'Зареждане…',
    empty: 'Няма данни',
    error: 'Грешка',
    retry: 'Опитай пак',
    all: 'Всички',
    none: 'Няма',
    from: 'От',
    to: 'До',
    date: 'Дата',
    time: 'Час',
    status: 'Състояние',
    actions: 'Действия',
    notes: 'Бележки',
    total: 'Общо',
    yes: 'Да',
    no: 'Не',
    unknown: 'Неизвестно',
    filters: 'Филтри',
    clearFilters: 'Изчисти филтрите',
    results: 'резултата',
    page: 'Страница',
    of: 'от',
    theme: 'Тема',
    logout: 'Изход',
    shortcuts: 'Клавишни комбинации',
    refresh: 'Обнови',
    copy: 'Копирай',
    copied: 'Копирано',
  },
  board: {
    title: 'Бусовете сега',
    subtitle: 'Клик върху бус — курсът, заявките, документите и шофьорът му',
    vehicle: 'Бус',
    driver: 'Шофьор',
    motion: 'Състояние',
    position: 'Позиция',
    currentOrder: 'Текуща заявка',
    nextStop: 'Следваща точка',
    eta: 'Пристига',
    hoursLeft: 'Оставащо кормуване',
    freeFrom: 'Свободен от',
    alerts: 'Сигнали',
    advisory: 'Помощно изчисление — меродавен е тахографът',
    live: 'На живо',
    reconnecting: 'Възстановяване на връзката…',
    polling: 'Резервно допитване на 20 секунди',
    offline: 'Няма връзка със сървъра',
    lastUpdate: 'Обновено',
    noVehicles: 'Няма бусове в парка.',
    unassignedOrders: 'Заявки без бус',
    assign: 'Назначи',
    idle: 'Свободен',
  },
  motion: {
    moving: 'Движи се',
    idle: 'На празен ход',
    stopped: 'Спрял',
    offline: 'Мълчи',
  },
  orders: {
    title: 'Заявки',
    number: 'Номер',
    customerRef: 'Номер на клиента',
    partner: 'Клиент',
    route: 'Направление',
    price: 'Цена',
    weight: 'Тегло',
    goods: 'Товар',
    deadline: 'Срок',
    createdAt: 'Създадена',
    newOrder: 'Нова заявка',
    editOrder: 'Редакция на заявка',
    stops: 'Адреси',
    addPickup: 'Добави товарене',
    addDelivery: 'Добави разтоварване',
    timeline: 'Хронология на адресите',
    documents: 'Документи',
    events: 'Събития',
    transitions: 'Промяна на състоянието',
    noTransitions: 'От това състояние няма разрешен преход.',
    hardDeadline: 'Твърд срок',
    adr: 'ADR',
    adrPoints: 'Точки по ADR 1.1.3.6',
    tailLift: 'Падащ борд',
    declaredValue: 'Обявена стойност (ЧМР чл. 24)',
    subcontractor: 'Подизпълнител',
    internalNotes: 'Вътрешни бележки',
    publicRef: 'Проследяване',
    assignVehicle: 'Назначи бус',
    noOrders: 'Няма заявки по тези филтри.',
    createdOk: 'Заявката е създадена.',
    updatedOk: 'Заявката е записана.',
    statusChanged: 'Състоянието е променено.',
  },
  dispatch: {
    candidates: 'Подходящи бусове',
    candidatesFor: 'Кой бус може да поеме тази заявка',
    emptyKm: 'Празни км',
    cost: 'Себестойност',
    margin: 'Марж',
    hoursRemaining: 'Оставащо кормуване',
    etaAtPickup: 'При товарния адрес',
    blocked: 'Забранен',
    blocks: 'Забрани',
    warnings: 'Предупреждения',
    score: 'Оценка',
    assignConfirmTitle: 'Назначаване на бус',
    assignConfirmBody: 'Заявката се назначава на буса и курсът тръгва към телефона на шофьора.',
    assigned: 'Бусът е назначен.',
    noCandidates: 'Няма свободен бус, който да поеме тази заявка.',
    blockedByTachograph: 'Тахографът не е G2V2 — международен курс е забранен',
    blockedByCabotage: 'Изчерпан каботаж в тази държава',
    blockedByDocuments: 'Изтекъл документ',
  },
  quote: {
    title: 'Оферта',
    subtitle: 'Двете числа преди „изпрати"',
    lane: 'Направление',
    fromPlace: 'Товарене',
    toPlace: 'Разтоварване',
    readyAt: 'Готово за товарене',
    deadlineAt: 'Краен срок за доставка',
    distance: 'Разстояние',
    emptyKm: 'Празен пробег',
    duration: 'Време за път',
    cost: 'Себестойност',
    suggestedPrice: 'Предложена цена',
    margin: 'Марж',
    breakdown: 'Разбивка на разхода',
    fuel: 'Гориво',
    tolls: 'Тол такси',
    driver: 'Шофьор',
    perDiem: 'Командировъчни',
    accommodation: 'Нощувки',
    fixed: 'Постоянни разходи',
    other: 'Други',
    calculate: 'Изчисли',
    send: 'Изпрати офертата',
    sent: 'Офертата е изпратена.',
    targetMargin: 'Целеви марж',
    warnings: 'Внимание',
    toOrder: 'Превърни в заявка',
  },
  trips: {
    title: 'Курсове',
    number: 'Курс',
    vehicle: 'Бус',
    driver: 'Шофьор',
    planned: 'Планиран',
    started: 'Тръгнал',
    completed: 'Приключил',
    legs: 'Отсечки',
    pnl: 'Приход и разход',
    revenue: 'Приход',
    cost: 'Себестойност',
    margin: 'Марж',
    loadedKm: 'Натоварени км',
    emptyKm: 'Празни км',
    emptyShare: 'Дял празен пробег',
    emptyCost: 'Цена на празния пробег',
    distance: 'Километри',
    kind: 'Вид',
    noTrips: 'Няма курсове по тези филтри.',
    emptyLegExplain:
      'Празните отсечки не носят приход, но носят гориво, тол и командировъчни. Тук се вижда къде отиде маржът.',
  },
  map: {
    title: 'Карта',
    vehicles: 'Бусове',
    replay: 'Маршрут на курс',
    geofences: 'Геозони',
    noKey: 'Липсва ключ за картата (VITE_MAPTILER_KEY) — картата е изключена.',
    selectTrip: 'Изберете курс',
    positions: 'точки',
  },
  partners: {
    title: 'Контрагенти',
    name: 'Наименование',
    vatNo: 'ДДС номер',
    country: 'Държава',
    city: 'Град',
    roles: 'Роли',
    isCustomer: 'Клиент',
    isSubcontractor: 'Подизпълнител',
    isSupplier: 'Доставчик',
    paymentTerms: 'Срок за плащане (дни)',
    creditLimit: 'Кредитен лимит',
    forbidsSubcontracting: 'Забранява преотдаване',
    newPartner: 'Нов контрагент',
  },
  /** Страницата „Места" е махната по желание на клиента; адресите в заявката се допълват оттук. */
  places: {
    search: 'Търсене на място',
    savedFirst: 'Запазените места излизат първи.',
  },
  vehicles: {
    title: 'Бусове',
    fleetNo: 'Номер',
    plate: 'Регистрация',
    makeModel: 'Марка и модел',
    massF1: 'Техническа маса (F.1)',
    massF2: 'Регистрирана маса (F.2)',
    tachograph: 'Тахограф',
    calibrationDue: 'Калибриране до',
    newVehicle: 'Нов бус',
    downRated: 'Свален бус — F.1 над 3500 кг',
    notG2v2: 'Тахографът не е G2V2 — забранен за международен превоз от 01.07.2026',
  },
  drivers: {
    title: 'Шофьори',
    name: 'Име',
    phone: 'Телефон',
    licence: 'Свидетелство',
    card: 'Карта за тахограф',
    medical: 'Медицинско',
    psycho: 'Психотест',
    adr: 'ADR свидетелство',
    newDriver: 'Нов шофьор',
    cardLeadTime: 'Издаването на карта от ИААА отнема около 30 дни — предупреждаваме на 60.',
  },
  documents: {
    title: 'Документи за преглед',
    queue: 'Опашка',
    type: 'Вид',
    order: 'Заявка',
    uploadedAt: 'Качен',
    capturedAt: 'Заснет',
    accept: 'Приеми',
    reject: 'Върни',
    rejectReason: 'Причина за връщане',
    rejectRequired: 'Въведете причина — шофьорът я вижда на телефона си.',
    accepted: 'Документът е приет.',
    rejected: 'Документът е върнат на шофьора.',
    rejectedReshoot: 'Върнат — шофьорът вижда задача да го заснеме наново.',
    reshoot: 'Шофьорът да го заснеме наново',
    reshootHint: 'Махнете отметката, ако хартията е в офиса или вече има добра снимка.',
    driverConfirmed: 'Потвърдена от шофьора',
    replaces: 'Заменя върната снимка',
    emptyQueue: 'Опашката е празна. Всички документи са прегледани.',
    keyboardHint: 'A — приеми · R — върни · J/K — следващ/предишен',
    preview: 'Изображение',
    noPreview: 'Няма визуализация за този файл.',
    ocr: 'Разчетено',
    sha: 'Контролна сума',
  },
  reports: {
    title: 'Справки',
    revenue: 'Приход',
    cost: 'Разход',
    margin: 'Марж',
    marginPct: 'Марж %',
    orders: 'Заявки',
    trips: 'Курсове',
    km: 'Километри',
    emptyKmShare: 'Дял празен пробег',
    perKm: 'Приход на километър',
    topPartners: 'Най-големи клиенти',
    byMonth: 'По месеци',
    lanes: 'Направления',
    fleetUse: 'Натовареност на парка',
    period: 'Период',
    last12: 'Последните 12 месеца',
    thisMonth: 'Този месец',
    lastMonth: 'Миналият месец',
    thisYear: 'Тази година',
  },
  auth: {
    login: 'Вход',
    email: 'Имейл',
    password: 'Парола',
    totp: 'Код от приложението',
    signIn: 'Влез',
    failed: 'Входът е неуспешен.',
    sessionExpired: 'Сесията изтече. Влезте отново.',
    mfaRequired: 'Въведете шестцифрения код от приложението за автентикация.',
  },
  errors: {
    network: 'Няма връзка със сървъра.',
    unauthorized: 'Нямате право за това действие.',
    notFound: 'Записът не е намерен.',
    conflict: 'Записът е променен от друг потребител.',
    validation: 'Данните не са попълнени правилно.',
    server: 'Сървърна грешка. Опитайте отново.',
    unknown: 'Неочаквана грешка.',
    required: 'Полето е задължително.',
  },
} as const;

export type Dictionary = typeof bg;

/** ─── Етикети на изброимите стойности ────────────────────────────────── */

export const ORDER_STATUS_BG: Record<OrderStatus, string> = {
  draft: 'Чернова',
  confirmed: 'Потвърдена',
  planned: 'Планирана',
  assigned: 'Възложена',
  loaded: 'Натоварена',
  in_progress: 'В движение',
  delivered: 'Доставена',
  documents_pending: 'Чака документи',
  ready_to_invoice: 'За фактуриране',
  invoiced: 'Фактурирана',
  paid: 'Платена',
  closed: 'Приключена',
  cancelled: 'Анулирана',
};

export const TRIP_STATUS_BG: Record<TripStatus, string> = {
  draft: 'Чернова',
  planned: 'Планиран',
  dispatched: 'Изпратен',
  accepted: 'Приет',
  in_progress: 'В движение',
  completed: 'Приключен',
  cancelled: 'Анулиран',
};

export const STOP_KIND_BG: Record<StopKind, string> = {
  pickup: 'Товарене',
  delivery: 'Разтоварване',
};

export const STOP_STATUS_BG: Record<StopStatus, string> = {
  pending: 'Предстои',
  en_route: 'На път',
  arrived: 'Пристигнал',
  completed: 'Изпълнен',
  failed: 'Отказан',
};

export const LEG_KIND_BG: Record<LegKind, string> = {
  loaded: 'С товар',
  empty: 'Празна',
  repositioning: 'Преместване',
  return_home: 'Връщане в базата',
};

export const DOCUMENT_TYPE_BG: Record<DocumentType, string> = {
  cmr: 'ЧМР',
  pod: 'Разписка за доставка',
  delivery_note: 'Стокова разписка',
  packing_list: 'Опаковъчен лист',
  invoice: 'Фактура',
  purchase_invoice: 'Фактура от доставчик',
  fuel_receipt: 'Касов бон за гориво',
  toll_receipt: 'Тол такса',
  ferry_ticket: 'Ферибот',
  weighbridge: 'Кантар',
  photo_loading: 'Снимка при товарене',
  photo_damage: 'Снимка на щета',
  photo_seal: 'Снимка на пломба',
  customs: 'Митнически документ',
  insurance: 'Застраховка',
  vehicle_document: 'Документ на буса',
  driver_document: 'Документ на шофьора',
  transport_order: 'Заявка от клиента',
  odometer: 'Километраж',
  vehicle_registration: 'Талон на буса',
  id_card: 'Лична карта',
  driving_licence: 'Шофьорска книжка',
  medical_certificate: 'Медицинско',
  criminal_record: 'Свидетелство за съдимост',
  bank_details: 'Банкова сметка',
  certificate: 'Сертификат',
  posting_certificate: 'Удостоверение Macron',
  vignette: 'Винетка',
  other: 'Друго',
};

export const TRIP_EVENT_BG: Record<TripEventType, string> = {
  created: 'Създаден',
  dispatched: 'Изпратен към шофьора',
  accepted: 'Приет от шофьора',
  rejected: 'Отказан от шофьора',
  started: 'Тръгна',
  arrived_pickup: 'Пристигна за товарене',
  loaded: 'Натоварен',
  departed: 'Потегли',
  border_crossed: 'Пресечена граница',
  break_started: 'Начало на прекъсване',
  rest_started: 'Начало на почивка',
  arrived_delivery: 'Пристигна за разтоварване',
  unloaded: 'Разтоварен',
  pod_captured: 'Заснета разписка',
  completed: 'Приключен',
  delayed: 'Закъснение',
  incident: 'Произшествие',
  cancelled: 'Анулиран',
  note: 'Бележка',
  ecmr_signed: 'Подписана е-ЧМР',
};

export const TACHOGRAPH_BG: Record<TachographGeneration, string> = {
  none: 'Без тахограф',
  analogue: 'Аналогов',
  digital: 'Цифров',
  smart_v1: 'Интелигентен, първо поколение',
  smart_v2: 'Интелигентен G2V2',
  unknown: 'Неизвестен',
};

/** Държавите, в които реално се вози. Останалите се показват с кода си. */
export const COUNTRY_BG: Record<string, string> = {
  AT: 'Австрия',
  BE: 'Белгия',
  BG: 'България',
  HR: 'Хърватия',
  CY: 'Кипър',
  CZ: 'Чехия',
  DK: 'Дания',
  EE: 'Естония',
  FI: 'Финландия',
  FR: 'Франция',
  DE: 'Германия',
  GR: 'Гърция',
  HU: 'Унгария',
  IE: 'Ирландия',
  IT: 'Италия',
  LV: 'Латвия',
  LT: 'Литва',
  LU: 'Люксембург',
  MT: 'Малта',
  NL: 'Нидерландия',
  PL: 'Полша',
  PT: 'Португалия',
  RO: 'Румъния',
  SK: 'Словакия',
  SI: 'Словения',
  ES: 'Испания',
  SE: 'Швеция',
  CH: 'Швейцария',
  GB: 'Обединено кралство',
  RS: 'Сърбия',
  TR: 'Турция',
  NO: 'Норвегия',
  MK: 'Северна Македония',
  UA: 'Украйна',
  MD: 'Молдова',
};

export function countryName(code: string | null | undefined): string {
  if (!code) return '—';
  const upper = code.toUpperCase();
  return COUNTRY_BG[upper] ?? upper;
}

/** Срокове на документите на бусовете и шофьорите — с думи, не с кодове. */
export const EXPIRY_KIND_BG: Record<string, string> = {
  insurance_mtpl: 'Гражданска отговорност',
  insurance_casco: 'Каско',
  insurance_cmr: 'ЧМР застраховка',
  inspection: 'Технически преглед',
  vehicle_tax: 'Данък',
  vignette: 'Винетка',
  toll_account: 'Тол сметка',
  licence_copy: 'Копие на лиценза',
  lez_registration: 'Екозона',
  driver_licence: 'Книжка',
  adr_certificate: 'ADR',
  medical: 'Медицинско',
  psycho_test: 'Психотест',
  driver_attestation: 'Удостоверение',
  financial_standing: 'Финансова стабилност',
  other: 'Друго',
};
